import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, shareApiPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardList, FileDown, Edit2, History, Share2, FileCheck2, Ban, Mail, MailCheck, Search, PackageMinus } from "lucide-react";
import { usePagedList, PaginationBar } from "@/components/Pagination";
import { Input } from "@/components/ui/input";

const ESTADO_OPTS = [
  { v: "todos", l: "Todos los estados" },
  { v: "pendiente", l: "Pendiente" },
  { v: "facturado", l: "Facturado" },
  { v: "cancelado", l: "Cancelado" },
];

export default function SalesOrders() {
  const [items, setItems] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [filterEstado, setFilterEstado] = useState("todos");
  const [q, setQ] = useState("");  // ITER189: barra de búsqueda por número + cliente
  const filtered = useMemo(() => {
    let res = items;
    if (filterEstado !== "todos") res = res.filter(it => (it.estado || "pendiente") === filterEstado);
    const term = q.trim().toLowerCase();
    if (term) {
      res = res.filter(it =>
        (it.numero || "").toLowerCase().includes(term) ||
        (it.cliente_nombre || "").toLowerCase().includes(term) ||
        (it.cliente_razon_social || "").toLowerCase().includes(term) ||
        (it.cliente_nombre_comercial || "").toLowerCase().includes(term)
      );
    }
    return res;
  }, [items, filterEstado, q]);
  const pagedSalesOrders = usePagedList(filtered, 20);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/sales/pedidos");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const editar = (it) => {
    if (it.estado && it.estado !== "pendiente") {
      return toast.error(`No se puede editar un pedido en estado '${it.estado}'`);
    }
    navigate(`/pedidos/${it.id}/editar`);
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar ${it.numero}?`)) return;
    try { await api.delete(`/sales/pedidos/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelar = async (it) => {
    if (!window.confirm(
      `¿Cancelar la nota de pedido ${it.numero}?\n\n` +
      `Esto LIBERA la reserva de stock para que otros vendedores puedan facturar esos productos. ` +
      `El pedido se conserva en estado "cancelado" para auditoría (el stock físico del lote NO se modifica).`
    )) return;
    try {
      await api.patch(`/sales/pedidos/${it.id}/cancelar`);
      toast.success(`Pedido ${it.numero} cancelado · reserva liberada`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const darDeBaja = async (it) => {
    const motivo = window.prompt(
      `DAR DE BAJA — Nota de pedido ${it.numero}\n\n` +
      `Esta acción DESCUENTA físicamente el stock del/los lote(s) reservados en esta NP, SIN generar factura.\n` +
      `Usar para: rotura, muestra médica, expiración, uso interno, entrega administrativa.\n\n` +
      `Indique el motivo (mínimo 5 caracteres):`,
      ""
    );
    if (motivo === null) return;
    if ((motivo || "").trim().length < 5) return toast.error("El motivo debe tener al menos 5 caracteres");
    try {
      const { data } = await api.patch(`/sales/pedidos/${it.id}/dar-de-baja`, { motivo: motivo.trim() });
      const detalle = (data?.detalle || []).map(d => `${d.numero_lote}: ${d.cantidad}u`).join(", ");
      toast.success(
        `Pedido ${it.numero} dado de baja · ${data?.movimientos_generados || 0} movimiento(s) de salida${detalle ? ` (${detalle})` : ""}`,
        { duration: 6000 }
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (it) => {
    try {
      const r = await api.get(`/sales/pedidos/${it.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `NotaPedido_${it.numero}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sharePdf = async (it) => {
    try {
      const r = await shareApiPdf(`/sales/pedidos/${it.id}/pdf`, {
        filename: `NotaPedido_${it.numero}.pdf`,
        title: `Nota de Pedido ${it.numero}`,
        text: `Confirmo su pedido ${it.numero} por $${(it.total || 0).toFixed(2)}. Adjunto el detalle en PDF.`,
      });
      if (r.fallback === "downloaded") toast.message("PDF descargado · Adjúntelo manualmente en WhatsApp");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const notificarVentas = async (it) => {
    if (!window.confirm(
      `¿Notificar a Secretaría de Ventas (sec.ventas@pharma-stern.com) sobre la Nota de Pedido ${it.numero}?\n\n` +
      `Cliente: ${it.cliente_nombre || "—"}\n` +
      `Total: $${(it.total || 0).toFixed(2)}\n\n` +
      `Recibirán un correo con el detalle para que procedan a facturar.`
    )) return;
    try {
      const { data } = await api.post(`/sales/pedidos/${it.id}/notificar-ventas`);
      toast.success(`Correo enviado a ${(data.destinatarios || []).join(", ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Ventas</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Notas de pedido</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Las notas de pedido validan stock disponible. Pueden crearse directamente o convertirse desde una cotización aprobada.</p>
        </div>
        <Button onClick={() => navigate("/pedidos/nuevo")} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-order-btn">
          <Plus size={16} className="mr-2" /> Nueva nota de pedido
        </Button>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <label className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold block mb-1">Estado</label>
          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger data-testid="order-filter-estado" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ESTADO_OPTS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[260px] flex-1">
          <label className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold block mb-1">Buscar</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-[#8ba397]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Número, cliente (razón social o nombre comercial)…"
              className="h-9 pl-8"
              data-testid="order-search-input"
            />
          </div>
        </div>
        <div className="text-xs text-[#5c7368] ml-auto">
          Mostrando <b>{filtered.length}</b> de {items.length} pedidos
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-x-auto">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Vendedor</th><th className="text-right">Subtotal</th><th className="text-right">IVA</th><th className="text-right">Total</th><th>Estado</th><th># Líneas</th><th className="w-24"></th></tr></thead>
          <tbody>
            {pagedSalesOrders.paged.map(it => (
              <tr key={it.id} data-testid={`order-row-${it.id}`}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td className="font-medium">{it.cliente_nombre}</td>
                <td className="text-[#4a5c53]">{it.vendedor_nombre}</td>
                <td className="text-right tabular-nums">${(it.subtotal ?? Math.max(0, (it.total || 0) - (it.iva_total || 0))).toFixed(2)}</td>
                <td className="text-right tabular-nums text-[#5c7368]">${(it.iva_total || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${(it.total || 0).toFixed(2)}</td>
                <td><Badge variant="outline" className="capitalize">{it.estado}</Badge></td>
                <td>{it.lineas?.length || 0}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {(it.edit_history || []).length > 0 && (
                      <Button size="icon" variant="ghost" onClick={() => setHistoryOpen(it)} title="Historial" data-testid={`order-history-${it.id}`}>
                        <History size={14} className="text-[#205c45]" />
                      </Button>
                    )}
                    {(!it.estado || it.estado === "pendiente") && (
                      <Button size="icon" variant="ghost" onClick={() => editar(it)} title="Editar" data-testid={`order-edit-${it.id}`}>
                        <Edit2 size={14} className="text-[#205c45]" />
                      </Button>
                    )}
                    {(!it.estado || it.estado === "pendiente") && (
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => notificarVentas(it)}
                        title={it.notificado_ventas
                          ? `Re-notificar a Secretaría de Ventas (último envío: ${(it.notificado_ventas_at || "").slice(0, 16).replace("T", " ")})`
                          : "Notificar a Secretaría de Ventas para que facturen"}
                        data-testid={`order-notify-${it.id}`}
                      >
                        {it.notificado_ventas ? <MailCheck size={14} className="text-[#205c45]" /> : <Mail size={14} className="text-[#2d4a73]" />}
                      </Button>
                    )}
                    {(!it.estado || it.estado === "pendiente") && (
                      <Button
                        size="sm"
                        className="bg-[#205c45] hover:bg-[#1a4a37] h-7 px-2"
                        onClick={() => navigate(`/facturacion/nueva?pedido=${it.id}`)}
                        title="Generar factura electrónica desde este pedido"
                        data-testid={`order-invoice-${it.id}`}
                      >
                        <FileCheck2 size={14} className="mr-1" /> Facturar
                      </Button>
                    )}
                    {(!it.estado || it.estado === "pendiente") && (
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => cancelar(it)}
                        title="Cancelar (libera la reserva · el stock físico no se modifica)"
                        data-testid={`order-cancel-${it.id}`}
                      >
                        <Ban size={14} className="text-[#b08400]" />
                      </Button>
                    )}
                    {(!it.estado || it.estado === "pendiente") && (
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => darDeBaja(it)}
                        title="Dar de baja (descuenta físicamente el stock del lote · sin generar factura)"
                        data-testid={`order-baja-${it.id}`}
                      >
                        <PackageMinus size={14} className="text-[#b53a25]" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => sharePdf(it)} title="Compartir por WhatsApp / Email" data-testid={`order-share-${it.id}`}>
                      <Share2 size={16} className="text-[#205c45]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => downloadPdf(it)} title="Descargar PDF" data-testid={`order-pdf-${it.id}`}>
                      <FileDown size={16} className="text-[#2d4a73]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)} title="Eliminar" data-testid={`order-delete-${it.id}`}>
                      <Trash2 size={16} className="text-[#b53a25]" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-14 text-[#8ba397]">
                <ClipboardList className="inline mb-2" size={28} /><br />
                {items.length === 0
                  ? "Sin notas de pedido. Cree una nueva o conviértala desde Cotización."
                  : `Sin notas de pedido en estado "${filterEstado}"`}
              </td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar page={pagedSalesOrders.page} total={pagedSalesOrders.total} pageSize={pagedSalesOrders.pageSize} onPrev={pagedSalesOrders.prev} onNext={pagedSalesOrders.next} label="notas de pedido" />
      </div>

      <Dialog open={!!historyOpen} onOpenChange={(v) => { if (!v) setHistoryOpen(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle><History className="inline mr-2" size={18} />Historial · {historyOpen?.numero}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(historyOpen?.edit_history || []).length === 0 && <p className="text-sm text-[#5c7368]">No hay ediciones registradas.</p>}
            {(historyOpen?.edit_history || []).map((h, idx) => (
              <div key={`${h.fecha || idx}-${h.user_id || h.user_nombre || idx}`} className="border-l-2 border-[#205c45] pl-3 py-2 bg-[#f7f9f8] rounded">
                <div className="text-xs text-[#5c7368]">{(h.fecha || "").slice(0, 16).replace("T", " ")} · <b>{h.user_nombre}</b></div>
                <ul className="text-sm mt-1 space-y-0.5 list-disc pl-5">
                  {(h.cambios || []).map((c, i) => <li key={`${idx}-${i}-${(c || "").slice(0,12)}`}>{c}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
